import type { intelligence } from '@cwos/api-client';
import { periodPhrase } from './evidence.js';

/**
 * Pure formatting and derivation for Intelligence result objects.
 *
 * Everything here is a function of its typed argument — no clocks, no locale,
 * no DOM — so the honesty rules the result components keep can be tested
 * without rendering anything, and so the same payload always produces the same
 * glyphs. The components below this file decide layout; they never decide what
 * a number MEANS.
 *
 * The two rules this module exists to enforce:
 *
 *  1. A `null` measure is never a zero. It is one of two different sentences —
 *     WITHHELD (too few people stand behind it, named in
 *     `disclosure.suppression`) or NOT RECORDED (the sources were consulted and
 *     had nothing) — and {@link measureState} is the single place that decides
 *     which. No component re-derives it.
 *  2. A direction is not a verdict. A rise in hours worked and a rise in missed
 *     shifts are the same arrow and opposite news, so {@link comparisonView}
 *     returns a direction and never a tone. Colouring it would be the component
 *     inventing an opinion the server did not send.
 */

/**
 * Deterministic number formatting with grouping separators — the SAME
 * derivation the server's answer composer uses
 * (`intelligence/analytics/compose.ts` `formatValue`), so the number in the
 * KPI tile and the number in the relayed sentence are formed the same way and
 * cannot disagree by a digit. Deliberately not `toLocaleString`: a figure that
 * renders differently per machine is not evidence.
 */
export function formatNumber(value: number, precision: number): string {
  const fixed = value.toFixed(precision);
  const [whole, fraction] = fixed.split('.');
  const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction !== undefined ? `${grouped}.${fraction}` : grouped;
}

/**
 * The unit as it appears AFTER the number. `count` has no unit word — the
 * metric's own label supplies the noun ("12 open exceptions"), so appending
 * one here would say it twice.
 */
export function unitSuffix(unit: intelligence.MetricUnit): string {
  switch (unit) {
    case 'hours':
      return ' hours';
    case 'percent':
      return '%';
    case 'count':
      return '';
  }
}

/** "1,284.5 hours" / "94.2%" / "12". */
export function formatMeasure(
  value: number,
  unit: intelligence.MetricUnit,
  precision: number,
): string {
  return `${formatNumber(value, precision)}${unitSuffix(unit)}`;
}

/**
 * What a measure IS, before anybody decides how to draw it.
 *
 * The `withheld` arm carries no number at all — not even an optional one — for
 * the reason `FigurePhase` gives in `features/figureState.ts`: the moment a
 * zero exists on a non-value arm, a call site reads it.
 */
export type MeasureState =
  | { kind: 'value'; value: number }
  /**
   * Suppressed for small-N. A real figure, withheld from this caller.
   *
   * CARRIES THE FLOOR AND NOT THE COHORT (F359). It used to carry both. The
   * cohort was an exact headcount of a population this caller cannot name,
   * over a window this caller chose, and it is gone from the wire; it is gone
   * from this type too rather than left optional, because a renderer cannot
   * print what the state does not hold. `minCohortSize` stays: it is a
   * published constant and it is what makes the blank legible as a rule.
   */
  | { kind: 'withheld'; minCohortSize: number | null }
  /** The sources were consulted and had nothing to report. */
  | { kind: 'absent' };

/**
 * The one decision every nullable measure passes through.
 *
 * A suppressed figure and an unrecorded one both arrive as `null`, and the
 * difference is NOT recoverable from the value — only the disclosure envelope
 * knows. Rendering either as `0` is the defect this product ranks worst;
 * rendering a suppressed one as "nothing was recorded" is the same defect with
 * better manners, because it tells the reader an organisation did nothing when
 * in fact they were not allowed to see what it did.
 */
export function measureState(
  value: number | null,
  disclosure: intelligence.ResultDisclosure | undefined,
  field: string,
): MeasureState {
  if (value !== null) return { kind: 'value', value };
  if (disclosure !== undefined && isSuppressed(disclosure, field)) {
    return {
      kind: 'withheld',
      minCohortSize: disclosure.suppression?.minCohortSize ?? null,
    };
  }
  return { kind: 'absent' };
}

/**
 * THE ONE QUESTION THIS MODULE ASKS OF THE ENVELOPE, and after F359 the only
 * one: was this field suppressed? A cell's presence is the whole answer.
 *
 * `cohortSizeOf` stood beside this and is deleted. It read the exact headcount
 * out of the cell so `withheldSentence` could print it; the server no longer
 * sends it and no sentence may print it. Note that `isSuppressed` never
 * depended on the count — a cell WITHOUT a cohort is still a suppression, and
 * that is precisely why the count could leave without withheld collapsing into
 * absent.
 */
function isSuppressed(disclosure: intelligence.ResultDisclosure, field: string): boolean {
  return (disclosure.suppression?.cells ?? []).some((cell) => cell.fields.includes(field));
}

/**
 * Why a figure is blank, in the reader's words rather than the schema's.
 *
 * NAMES THE FLOOR, NOT THE COHORT (F359). This sentence used to end "This one
 * stands on 3." — the server published the cohort size precisely so a UI could
 * say WHY, and naming it did turn a blank cell from a bug into an explanation.
 * What it also did was hand an exact headcount to a caller who cannot see whose
 * heads they are, about a window that caller picked to the day; walk the
 * calendar and the sentences are a census.
 *
 * WHAT THE READER ACTUALLY NEEDED, and still gets, is three things: that a
 * figure EXISTS here, that a RULE and not a fault is why they cannot see it,
 * and that the blank is NOT A ZERO. The floor supplies the second; the word
 * "withheld" supplies the first and third. The headcount supplied none of them.
 * Both sentences still end by ruling out the reading they exist to prevent.
 */
export function withheldSentence(state: Extract<MeasureState, { kind: 'withheld' }>): string {
  const floor =
    state.minCohortSize !== null
      ? `Figures standing on fewer than ${state.minCohortSize} people are withheld`
      : 'Figures standing on too few people are withheld';
  return `${floor} rather than shown. It is withheld, not zero.`;
}

/** The counterpart sentence for a measure the sources simply did not have. */
export const ABSENT_SENTENCE =
  'The records behind this measure were consulted and none fell in this period. Nothing was withheld from you.';

// ---------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------

export interface ComparisonView {
  /** '↑' | '↓' | '→' — the direction, and nothing about whether it is good. */
  arrow: '↑' | '↓' | '→';
  /**
   * The direction as a WORD, and the same word the server's own composer uses
   * ("higher" / "lower"). It is rendered visibly rather than hidden behind the
   * glyph: an arrow has no reliable spoken form, so a delta whose direction
   * lives only in the glyph is a delta a screen reader announces as a bare
   * percentage — the magnitude without the sign.
   */
  word: 'higher' | 'lower' | 'unchanged';
  /** "6.2%" or, when no percentage is knowable, the absolute delta. */
  magnitude: string;
  /** "vs previous week" / "vs 2026-08-10 – 2026-08-16". */
  against: string;
  /** The whole line, composed deterministically. */
  text: string;
}

function comparisonLine(
  arrow: ComparisonView['arrow'],
  word: ComparisonView['word'],
  magnitude: string,
  against: string,
): ComparisonView {
  return {
    arrow,
    word,
    magnitude,
    against,
    text: word === 'unchanged' ? `Unchanged ${against}` : `${magnitude} ${word} ${against}`,
  };
}

/**
 * The delta line, or null.
 *
 * Null in three cases, all of them honest: no comparison was requested; the
 * previous period has no recorded value (there is nothing to be a percentage
 * OF); or this period's own value is absent. A comparison assembled from one
 * real number and one missing one is a fabrication with an arrow on it, which
 * is exactly what the server's own composer refuses to emit.
 */
export function comparisonView(
  kpi: intelligence.AnalyticsKpi,
  previousPeriodLabel?: string,
): ComparisonView | null {
  const comparison = kpi.comparison;
  if (comparison === undefined) return null;
  if (kpi.value === null || comparison.previousValue === null) return null;

  const against =
    previousPeriodLabel !== undefined && previousPeriodLabel.trim() !== ''
      ? `vs ${previousPeriodLabel}`
      : `vs ${comparison.previousPeriod.from} – ${comparison.previousPeriod.to}`;

  const percent = comparison.deltaPercent;
  if (percent !== null && Number.isFinite(percent)) {
    return comparisonLine(
      percent > 0 ? '↑' : percent < 0 ? '↓' : '→',
      percent > 0 ? 'higher' : percent < 0 ? 'lower' : 'unchanged',
      `${formatNumber(Math.abs(percent), 1)}%`,
      against,
    );
  }
  // A previous value of exactly zero makes a percentage undefined, not
  // infinite. The absolute delta is still a real, computed number, so it is
  // reported instead of dropping the comparison entirely.
  const delta = comparison.delta;
  if (delta === null || !Number.isFinite(delta)) return null;
  return comparisonLine(
    delta > 0 ? '↑' : delta < 0 ? '↓' : '→',
    delta > 0 ? 'higher' : delta < 0 ? 'lower' : 'unchanged',
    formatMeasure(Math.abs(delta), kpi.unit, kpi.precision),
    against,
  );
}

/**
 * "Last week · 33 workers" — the resolved period and who stands behind the
 * number, which are the two things that decide whether a figure may be quoted.
 *
 * The period falls back to the AUTHORITATIVE resolved boundaries the executor
 * returned rather than to a guess, because "last week" without boundaries is
 * the ambiguity the analytics contract exists to remove.
 *
 * Those boundaries are spelled "Aug 17–23" rather than `2026-08-17 – 2026-08-23`
 * (`periodPhrase` → `evidenceView.formatPeriod`). The wire spelling was not a
 * leaked identifier, but it is the wire's spelling on a line a person reads,
 * and one date format across the surface is worth more than the four extra
 * characters of a year nobody is in doubt about. When a boundary is not a date
 * this can trust, the raw pair is still shown — a period the reader can see is
 * odd beats a period silently dropped.
 */
export function subjectLine(kpi: intelligence.AnalyticsKpi): string {
  const period =
    periodPhrase(kpi.period, kpi.periodLabel) ?? `${kpi.period.from} – ${kpi.period.to}`;
  if (kpi.subjectCount === undefined) return period;
  const noun = kpi.subjectNoun ?? (kpi.subjectCount === 1 ? 'worker' : 'workers');
  return `${period} · ${formatNumber(kpi.subjectCount, 0)} ${noun}`;
}

// ---------------------------------------------------------------------------
// Chart derivation
// ---------------------------------------------------------------------------

/** Points with a real number in them — the only ones that can be plotted. */
export function plottablePoints(series: readonly intelligence.ResultChartSeries[]): number {
  return series.reduce(
    (total, one) => total + one.points.filter((point) => point.value !== null).length,
    0,
  );
}

/**
 * True when the payload cannot be plotted honestly and the table must be shown
 * instead.
 *
 * A "trend" through one point is a dot a reader will read as a line; a bar
 * chart of one bar has no comparison in it at all. Two real values is the floor
 * for a shape to carry any information, so below that the same data is rendered
 * as the table it actually is. The chart is not degraded — it is declined.
 */
export function tooSparseToPlot(chart: intelligence.ResultChart): boolean {
  return plottablePoints(chart.series) < 2;
}

/**
 * What a `null` at one chart point MEANS — {@link measureState}, asked the
 * question a point can answer.
 *
 * F228. A chart point used to carry only a label and a value, so a `null` bar
 * was un-joinable: WITHHELD and NOT RECORDED both drew the same gap, and the
 * picture said the weaker of the two sentences about a figure the server had
 * actually measured. `ResultChartPoint.key` closed that — the analytics engine
 * names each blanked dimension row by its own key in
 * `disclosure.suppression.cells[].fields`, so a keyed point can look itself up
 * in the disclosure the payload already carries.
 *
 * DELEGATES rather than re-deciding. `measureState` is this module's single
 * authority on the withheld/absent fork and the KPI tile already runs through
 * it; a second copy of that logic here is how a chart and the KPI beside it
 * start disagreeing about the same `null`.
 *
 * THE UN-KEYED ARM IS DELIBERATE AND IT IS `absent`. A point with no key —
 * an older payload, or a series whose dimension the disclosure is not
 * expressed in — cannot be looked up, and guessing "withheld" from the mere
 * presence of a suppression block elsewhere in the response would attribute a
 * cohort to a row the server never named. Un-joinable degrades to the honest
 * weaker claim; what it must never degrade to is a zero, and it does not.
 */
export function chartPointState(
  point: intelligence.ResultChartPoint | undefined,
  disclosure: intelligence.ResultDisclosure | undefined,
): MeasureState {
  if (point === undefined) return { kind: 'absent' };
  if (point.value !== null) return { kind: 'value', value: point.value };
  if (point.key === undefined) return { kind: 'absent' };
  return measureState(null, disclosure, point.key);
}

/**
 * The chart's own data as a table, derived — never a second payload.
 *
 * Used for the too-sparse fallback and for the disclosure a chart cannot make
 * visually: a `null` point becomes a real cell arm in the table, and the table
 * is the version a screen reader can actually read.
 *
 * F228 — THE FORK THIS FUNCTION NOW MAKES, AND WHY IT IS NOT COSMETIC.
 *
 * It used to map every `null` to `{ kind: 'absent' }` — the em dash that means
 * "the server answered and had nothing here". For a small-N SUPPRESSED row
 * that was simply the wrong sentence: the server did have a figure, measured
 * it, and withheld it because too few people stand behind it. Printing "—"
 * there tells the reader nothing was recorded, which is the same class of
 * error as printing `0`, only quieter.
 *
 * The disclosure has always known which rows those are. What was missing was a
 * way to ask per point, and `ResultChartPoint.key` is it: the engine names
 * suppressed rows by their own dimension key, so a keyed point can look itself
 * up. `chartPointState` is the single place that fork is made — this function
 * and `ChartResult` both call it, so the table and the picture can never
 * disagree about which bar is which kind of blank.
 */
export function tableFromChart(chart: intelligence.ResultChart): intelligence.ResultTable {
  const labels: string[] = [];
  for (const series of chart.series) {
    for (const point of series.points) {
      if (!labels.includes(point.label)) labels.push(point.label);
    }
  }
  return {
    kind: 'table',
    title: chart.title,
    ...(chart.periodLabel !== undefined ? { caption: chart.periodLabel } : {}),
    columns: [
      { key: 'label', header: '', numeric: false, sortable: false },
      ...chart.series.map((series) => ({
        key: series.key,
        header: series.label,
        numeric: true,
        sortable: false,
      })),
    ],
    rows: labels.map((label) => ({
      id: label,
      cells: {
        label: { kind: 'text' as const, text: label },
        ...Object.fromEntries(
          chart.series.map((series) => {
            const point = series.points.find((candidate) => candidate.label === label);
            const state = chartPointState(point, chart.disclosure);
            if (state.kind === 'absent') return [series.key, { kind: 'absent' as const }];
            if (state.kind === 'withheld') {
              // NO COHORT RIDES ALONG (F359). It used to, so the cell could say
              // WHY rather than only THAT. `kind: 'withheld'` is itself the
              // why — it is a different arm from `absent` and from a zero
              // `number`, which is the distinction the reader needed; the
              // headcount was never part of it.
              return [series.key, { kind: 'withheld' as const }];
            }
            return [
              series.key,
              {
                kind: 'number' as const,
                value: state.value,
                precision: chart.precision,
                unit: chart.unit,
              },
            ];
          }),
        ),
      },
    })),
    ...(chart.disclosure !== undefined ? { disclosure: chart.disclosure } : {}),
  };
}

/**
 * A grouped analytics answer, as a chart — with NO new server field.
 *
 * `AnalyticsQueryResult.grouped` is `{ key, label, value: number | null }[]`,
 * which is exactly one bar series, so "break it down by program" and "show me"
 * are the same payload rendered two ways. Returns null when the answer carries
 * no grouping, because a KPI with one number is not a chart.
 */
export function chartFromKpi(kpi: intelligence.AnalyticsKpi): intelligence.ResultChart | null {
  const grouped = kpi.grouped ?? [];
  if (grouped.length === 0) return null;
  return {
    kind: 'chart',
    shape: 'bar',
    title: kpi.label,
    unit: kpi.unit,
    precision: kpi.precision,
    series: [
      {
        key: kpi.metric,
        label: kpi.label,
        // F228: the row's KEY travels with it. `AnalyticsGroupedRow.key` is
        // the same identifier the engine names in
        // `disclosure.suppression.cells[].fields` when it blanks a row, so
        // carrying it here is what lets a withheld bar be told from an absent
        // one downstream. Dropping it — which this function used to do — threw
        // away the only join between the `null` and the reason for it.
        //
        // The label falls back to the key when a dimension row has no name at
        // all, matching the server's own projection; the key field itself is
        // always the real key regardless.
        points: grouped.map((row) => ({
          key: row.key,
          label: row.label.trim() !== '' ? row.label : row.key,
          value: row.value,
        })),
      },
    ],
    ...(kpi.periodLabel !== undefined ? { periodLabel: kpi.periodLabel } : {}),
    ...(kpi.disclosure !== undefined ? { disclosure: kpi.disclosure } : {}),
  };
}

// ---------------------------------------------------------------------------
// Table sorting
// ---------------------------------------------------------------------------

export interface TableSort {
  key: string;
  direction: 'asc' | 'desc';
}

/**
 * Order rows by one column, with the un-orderable arms always LAST regardless
 * of direction.
 *
 * Withheld and absent cells have no position on a scale — sorting them to the
 * top of a descending column would present "we are not telling you" as the
 * largest value, and to the top of an ascending one as the smallest. Both are
 * arrangements the payload never asserted, so they sink to the bottom either
 * way and the reader can see the block of them.
 *
 * The sort is stable: equal cells keep payload order, so a re-sort of an
 * already-sorted column does not reshuffle ties into a new "ranking".
 */
export function sortRows(
  rows: readonly intelligence.ResultRow[],
  sort: TableSort | null,
): intelligence.ResultRow[] {
  if (sort === null) return [...rows];
  const rank = (row: intelligence.ResultRow): number | string | null => {
    const cell = row.cells[sort.key];
    if (cell === undefined) return null;
    switch (cell.kind) {
      case 'number':
        return cell.value;
      case 'text':
        return cell.text;
      case 'record':
        return cell.record.label;
      case 'status':
        return cell.label;
      case 'withheld':
      case 'absent':
        return null;
    }
  };
  return [...rows]
    .map((row, index) => ({ row, index, key: rank(row) }))
    .sort((a, b) => {
      if (a.key === null && b.key === null) return a.index - b.index;
      if (a.key === null) return 1;
      if (b.key === null) return -1;
      const compared =
        typeof a.key === 'number' && typeof b.key === 'number'
          ? a.key - b.key
          : String(a.key).localeCompare(String(b.key));
      if (compared !== 0) return sort.direction === 'asc' ? compared : -compared;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}
